import { useViewActions } from '@mm/state/actions/viewActions';
import AttributeModel from '@mm/state/model/AttributeModel';
import PageGroupModel from '@mm/state/model/PageGroupModel';
import PageModel from '@mm/state/model/PageModel';
import SuggestionModel from '@mm/state/model/SuggestionModel';
import TriggerModel, { Phrase } from '@mm/state/model/TriggerModel';
import { selectCurrentUserIsAdmin } from '@mm/state/selectors/userSelector';
import { colors } from '@mm/utils';
import { ReactComponent as TrashIcon } from 'assets/icons/garbage.svg';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import 'react-toggle/style.css';
import { Switch, Tooltip } from '@mm/components';
import TriggerPhrases from '../TriggerPhrases/TriggerPhrases';
import TriggerResponseItem from '../TriggerResponseItem/TriggerResponseItem';
import './TriggerItem.scss';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { PrimaryToolTip } from '../../../../../common/components/PrimaryToolTip/PrimaryToolTip';

type Props = {
  onUpdate: (
    responseEntity: Partial<TriggerModel>,
  ) => Promise<TriggerModel | undefined>;
  onDelete: () => void;
  trigger: TriggerModel;
  existingPhrases: string[];
  suggestions: SuggestionModel[];
  pageGroups: PageGroupModel[];
  pages: PageModel[];
  attributes: AttributeModel[];
  activeChatbotId: number;
};

export const TriggerItem = ({
  onUpdate,
  onDelete,
  trigger,
  suggestions,
  existingPhrases,
  pages,
  pageGroups,
  attributes,
  activeChatbotId,
}: Props) => {
  const { phrases, botResponses: responses } = trigger;
  const isMMAdmin = useSelector(selectCurrentUserIsAdmin);
  const { showSlideoutMenu } = useViewActions();

  const onAddPhrase = (trigger: TriggerModel, phrase: string) => {
    onUpdate({
      ...trigger,
      phrases: [
        ...trigger.phrases,
        {
          id: null as unknown as number,
          sortOrder: phrases.length + 1,
          phrase,
        },
      ],
    });
  };

  const onUpdatePhrase = (trigger: TriggerModel, phrase: Phrase) => {
    const { phrases } = trigger;

    const updatedPhrases = phrases.map(p =>
      p.id === phrase.id ? { ...phrase, id: null as unknown as number } : p,
    );
    onUpdate({ ...trigger, phrases: updatedPhrases });
  };

  const onDeletePhrase = (index: number) => {
    const { phrases } = trigger;

    const newPhrases = [
      ...phrases.slice(0, index),
      ...phrases
        .slice(index + 1)
        .map(phrase => ({ ...phrase, sortOrder: phrase.sortOrder - 1 })),
    ];
    onUpdate({
      ...trigger,
      phrases: newPhrases,
    });
  };

  const onAddResponse = () => {
    const newSortOrder = Math.max(
      ...trigger.botResponses.map(response => response.sortOrder),
      0,
    );
    const newResponse = {
      id: null as unknown as number,
      responseText: 'Default text',
      pageId: null as unknown as number,
      sortOrder: newSortOrder + 1,
    };
    onUpdate({
      ...trigger,
      botResponses: [...trigger.botResponses, newResponse],
    });
  };

  const onUpdateResponse = (responseEntity: any) => {
    const { botResponses } = trigger;

    const responseIndex = botResponses.findIndex(
      resp => resp.sortOrder === responseEntity.sortOrder,
    );

    onUpdate({
      ...trigger,
      botResponses: [
        ...botResponses.slice(0, responseIndex),
        responseEntity,
        ...botResponses.slice(responseIndex + 1),
      ],
    });
  };

  const onDeleteResponse = (sortOrder: number) => {
    const { botResponses } = trigger;
    const responseIndex = botResponses.findIndex(
      resp => resp.sortOrder === sortOrder,
    );

    onUpdate({
      ...trigger,
      botResponses: [
        ...botResponses.slice(0, responseIndex),
        ...botResponses.slice(responseIndex + 1),
      ],
    });
  };

  const toggleActive = () => {
    onUpdate({
      ...trigger,
      active: !trigger.active,
    });
  };

  const toggleMultipleAnswers = () => {
    onUpdate({
      ...trigger,
      randomize: !trigger.randomize,
    });
  };

  const [updating, setUpdating] = useState(false);

  const isSessionStart = Array.isArray(trigger.eventType)
    ? trigger.eventType.includes('session_start')
    : trigger.eventType === 'session_start';

  const toggleEventType = async () => {
    setUpdating(true);

    try {
      await onUpdate({
        ...trigger,
        eventType: isSessionStart ? '' : 'session_start',
      });
    } finally {
      setUpdating(false);
    }
  };

  const onEditInSidebar = () => {
    showSlideoutMenu('qaEditor', { triggerId: trigger.id });
  };
  return (
    <div className="trigger-item">
      <div className="trigger-item__inner-container trigger-item__phrases-container">
        <div className="trigger-item__title">
          If user says something similar to:
        </div>
        <div>
          <TriggerPhrases
            trigger={trigger}
            suggestions={suggestions}
            existingPhrases={existingPhrases}
            onAdd={onAddPhrase}
            onUpdate={onUpdatePhrase}
            onRemove={onDeletePhrase}
          />
        </div>
      </div>
      <div className="trigger-item__inner-container--bigger trigger-item__answers-container">
        <>
          {responses.map(response => (
            <div key={response.sortOrder}>
              <TriggerResponseItem
                pages={pages}
                pageGroups={pageGroups}
                response={response}
                attributes={attributes}
                canBeDeleted={responses.length > 1}
                onUpdate={onUpdateResponse}
                onDelete={onDeleteResponse}
                activeChatbotId={activeChatbotId}
              />
            </div>
          ))}
        </>

        <div className="trigger-item__add-link" onClick={onAddResponse}>
          + Add answer
        </div>
      </div>
      <div className="trigger-item__controls-container">
        <div className="trigger-item__controls">
          <div>
            <label className="trigger-item__control">
              <span className="trigger-item__control-label">Status:</span>
              <Switch
                checked={trigger.active}
                disabled={trigger.phrases.length === 0}
                onChange={toggleActive}
                css={{ container: 'trigger-item__control--switch' }}
              />
            </label>
            <label className="trigger-item__control">
              <span className="trigger-item__control-label">Random:</span>
              <Switch
                checked={trigger.randomize}
                onChange={toggleMultipleAnswers}
                css={{ container: 'trigger-item__control--switch' }}
              />
            </label>

            <label className="trigger-item__control">
              <span className="trigger-item__control-label">
                Only use to start chat:
                <Tooltip
                  title="Don't detect this keyword in the middle of an active
                  conversation"
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>

              <Switch
                checked={isSessionStart}
                disabled={updating}
                onChange={toggleEventType}
                css={{ container: 'trigger-item__control--switch' }}
              />
            </label>
          </div>
          <div className="trigger-item__control--trash">
            <TrashIcon
              onClick={onDelete}
              className="trigger-item__delete_icon"
            />
          </div>
          {isMMAdmin && (
            <div
              style={{
                marginLeft: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                color: colors.accentBlue,
              }}
              onClick={onEditInSidebar}
            >
              <i className="fas fa-pen-square" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
